import AccountBalanceWalletRounded from '@mui/icons-material/AccountBalanceWalletRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import DoneAllRounded from '@mui/icons-material/DoneAllRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import InfoRounded from '@mui/icons-material/InfoRounded';
import Inventory2Rounded from '@mui/icons-material/Inventory2Rounded';
import NotificationsNoneRounded from '@mui/icons-material/NotificationsNoneRounded';
import PointOfSaleRounded from '@mui/icons-material/PointOfSaleRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import SavingsRounded from '@mui/icons-material/SavingsRounded';
import TrendingDownRounded from '@mui/icons-material/TrendingDownRounded';
import TrendingFlatRounded from '@mui/icons-material/TrendingFlatRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { api, unwrap, unwrapJson } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { money, moneyCompact, qty, relativeTime } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';
import { plural } from '../lib';
import { IconTile } from '../ui';
import {
  MarginTrend,
  PaymentMethods,
  SalesHeatmap,
  SalesVsPurchases,
  TopCustomers,
} from './InsightPanels';
import {
  addDays,
  agingBuckets,
  dashboardRanges,
  formatChange,
  greeting,
  intervalFor,
  isoDateIn,
  overdueAmount,
  percentChange,
  RANGE_OPTIONS,
  stockFill,
  stockSeverity,
  type AgingBucket,
  type AgingTotals,
  type RangeDays,
} from './lib';

const RANGE_KEY = 'sm.dashboard.range';

function initialRange(): RangeDays {
  try {
    const v = Number(localStorage.getItem(RANGE_KEY));
    return v === 7 || v === 90 ? v : 30;
  } catch {
    return 30;
  }
}

/**
 * The shop dashboard: KPIs with period-over-period change, sales trend, receivables/payables aging,
 * low stock, reminders and best sellers. Every panel is permission-aware.
 */
export function DashboardPage() {
  const { me, can } = useAuth();
  const { format } = useShop();
  const navigate = useNavigate();
  const [days, setDays] = useState<RangeDays>(initialRange);
  const today = isoDateIn(new Date(), format.timeZone);
  const { current, previous } = dashboardRanges(today, days);
  const interval = intervalFor(days);

  const reports = can('reports.view');
  const money$ = can('payments.view') || can('ledger.view');

  const sales = useQuery({
    queryKey: ['reports', 'sales-by-period', current, interval],
    queryFn: () =>
      unwrapJson(
        api.GET('/reports/sales/by-period', { params: { query: { ...current, interval } } }),
      ),
    enabled: reports,
  });
  const prevSales = useQuery({
    queryKey: ['reports', 'sales-by-period', previous, interval],
    queryFn: () =>
      unwrapJson(
        api.GET('/reports/sales/by-period', { params: { query: { ...previous, interval } } }),
      ),
    enabled: reports,
  });
  const twoDays = { from: addDays(today, -1), to: today };
  const daily = useQuery({
    queryKey: ['reports', 'sales-by-period', twoDays, 'day'],
    queryFn: () =>
      unwrapJson(
        api.GET('/reports/sales/by-period', {
          params: { query: { ...twoDays, interval: 'day' } },
        }),
      ),
    enabled: reports,
  });
  const profit = useQuery({
    queryKey: ['reports', 'gross-profit', current],
    queryFn: () =>
      unwrapJson(
        api.GET('/reports/gross-profit/by-period', {
          params: { query: { ...current, interval: 'month' } },
        }),
      ),
    enabled: reports && can('reports.financial'),
  });
  const receivable = useQuery({
    queryKey: ['reports', 'aging', 'RECEIVABLE', 5],
    queryFn: () =>
      unwrapJson(
        api.GET('/reports/aging', { params: { query: { ledger: 'RECEIVABLE', pageSize: 5 } } }),
      ),
    enabled: reports && money$,
  });
  const payable = useQuery({
    queryKey: ['reports', 'aging', 'PAYABLE', 5],
    queryFn: () =>
      unwrapJson(
        api.GET('/reports/aging', { params: { query: { ledger: 'PAYABLE', pageSize: 5 } } }),
      ),
    enabled: reports && money$,
  });
  const lowStock = useQuery({
    queryKey: ['reports', 'low-stock', 6],
    queryFn: () =>
      unwrapJson(api.GET('/reports/low-stock', { params: { query: { pageSize: 6 } } })),
    enabled: reports,
  });
  const best = useQuery({
    queryKey: ['reports', 'best-sellers', current],
    queryFn: () =>
      unwrapJson(
        api.GET('/reports/best-sellers', {
          params: { query: { ...current, by: 'revenue', limit: 5 } },
        }),
      ),
    enabled: reports,
  });

  const todayRow = daily.data?.data.find((d) => d.periodStart.slice(0, 10) === today);
  const yesterdayRow = daily.data?.data.find((d) => d.periodStart.slice(0, 10) !== today);
  const grossProfit = profit.data ? Number(profit.data.totals.grossProfit) : undefined;
  const margin = profit.data ? Number(profit.data.totals.marginPercent) : undefined;
  const firstName = me?.user.name.split(' ')[0] ?? '';
  const rangeLabel = RANGE_OPTIONS.find((o) => o.days === days)?.label ?? '';
  const trend = (sales.data?.data ?? []).map((d) => Number(d.total));

  const insight = { current, interval, rangeLabel };

  const pickRange = (d: RangeDays) => {
    setDays(d);
    try {
      localStorage.setItem(RANGE_KEY, String(d));
    } catch {
      // remembering the range is optional
    }
  };

  return (
    <>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ alignItems: { md: 'flex-end' }, justifyContent: 'space-between', mb: 3.5 }}
      >
        <Box>
          <Typography variant="h3">
            {greeting(new Date(), format.timeZone)}
            {firstName && `, ${firstName}`}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            {reports
              ? `Here’s how ${me?.tenant.name ?? 'your shop'} is doing over the last ${rangeLabel}.`
              : `Welcome to ${me?.tenant.name ?? 'your shop'}.`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
          {reports && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={days}
              onChange={(_, v: RangeDays | null) => v && pickRange(v)}
              aria-label="Period"
              sx={{
                bgcolor: 'background.paper',
                '& .MuiToggleButton-root': { px: 1.5, textTransform: 'none', fontWeight: 600 },
              }}
            >
              {RANGE_OPTIONS.map((o) => (
                <ToggleButton key={o.days} value={o.days}>
                  {o.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          {can('sales.create') && (
            <Button
              variant="contained"
              startIcon={<PointOfSaleRounded />}
              onClick={() => void navigate('/sales/counter')}
            >
              New sale
            </Button>
          )}
        </Stack>
      </Stack>

      {!reports ? (
        <Card>
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="h5">You’re all set</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Sales figures appear here for people whose role includes reports.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              gridTemplateColumns: {
                xs: 'minmax(0,1fr)',
                sm: 'repeat(2, minmax(0,1fr))',
                lg: 'repeat(4, minmax(0,1fr))',
              },
              mb: 2.5,
            }}
          >
            <Kpi
              label={`Sales · ${rangeLabel}`}
              value={sales.data && moneyCompact(sales.data.totals.total, format)}
              title={sales.data && money(sales.data.totals.total, format)}
              hint={sales.data && plural(sales.data.totals.invoiceCount, 'invoice')}
              change={percentChange(sales.data?.totals.total, prevSales.data?.totals.total)}
              changeHint={`vs previous ${rangeLabel}`}
              icon={<ReceiptLongRounded />}
              tone="primary"
              loading={sales.isLoading}
              spark={trend}
            />
            <Kpi
              label="Sales today"
              value={daily.data && money(todayRow?.total ?? 0, format)}
              hint={
                daily.data &&
                (todayRow?.invoiceCount
                  ? plural(todayRow.invoiceCount, 'invoice')
                  : 'No sales yet today')
              }
              change={percentChange(todayRow?.total, yesterdayRow?.total)}
              changeHint="vs yesterday"
              icon={<PointOfSaleRounded />}
              tone="success"
              loading={daily.isLoading}
            />
            <Kpi
              label="Customers owe you"
              value={receivable.data && moneyCompact(receivable.data.totals.outstanding, format)}
              title={receivable.data && money(receivable.data.totals.outstanding, format)}
              hint={
                receivable.data &&
                (overdueAmount(receivable.data.totals) > 0
                  ? `${money(overdueAmount(receivable.data.totals), format)} overdue`
                  : 'Nothing overdue')
              }
              hintTone={
                receivable.data && overdueAmount(receivable.data.totals) > 0 ? 'error' : undefined
              }
              icon={<AccountBalanceWalletRounded />}
              tone="warning"
              loading={receivable.isLoading}
              hidden={!money$}
            />
            {grossProfit !== undefined || profit.isLoading ? (
              <Kpi
                label={`Gross profit · ${rangeLabel}`}
                value={grossProfit !== undefined && moneyCompact(grossProfit, format)}
                title={grossProfit !== undefined && money(grossProfit, format)}
                hint={
                  margin !== undefined && Number(profit.data?.totals.netRevenue) > 0
                    ? `${String(Math.round(margin))}% margin`
                    : 'Revenue minus cost of goods'
                }
                icon={<SavingsRounded />}
                tone="info"
                loading={profit.isLoading}
              />
            ) : (
              <Kpi
                label="You owe suppliers"
                value={payable.data && moneyCompact(payable.data.totals.outstanding, format)}
                title={payable.data && money(payable.data.totals.outstanding, format)}
                hint={payable.data && `${String(payable.data.page.total)} suppliers`}
                icon={<Inventory2Rounded />}
                tone="info"
                loading={payable.isLoading}
                hidden={!money$}
              />
            )}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1.7fr) minmax(0,1fr)' },
              mb: 2.5,
            }}
          >
            <SalesTrend
              loading={sales.isLoading}
              rows={sales.data?.data ?? []}
              previous={prevSales.data?.data ?? []}
              interval={interval}
              rangeLabel={rangeLabel}
            />
            {money$ ? (
              <AgingPanel
                receivable={receivable.data}
                payable={payable.data}
                loading={receivable.isLoading || payable.isLoading}
              />
            ) : (
              <BestSellers
                loading={best.isLoading}
                rows={best.data?.data}
                rangeLabel={rangeLabel}
              />
            )}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              gridTemplateColumns: {
                xs: 'minmax(0,1fr)',
                md: 'repeat(2, minmax(0,1fr))',
                lg: 'repeat(3, minmax(0,1fr))',
              },
            }}
          >
            <LowStockPanel loading={lowStock.isLoading} data={lowStock.data} />
            <RemindersPanel />
            {money$ && (
              <BestSellers
                loading={best.isLoading}
                rows={best.data?.data}
                rangeLabel={rangeLabel}
              />
            )}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              mt: 2.5,
              gridTemplateColumns: {
                xs: 'minmax(0,1fr)',
                lg: can('products.view_cost') ? 'minmax(0,1.4fr) minmax(0,1fr)' : 'minmax(0,1fr)',
              },
            }}
          >
            {can('products.view_cost') && <SalesVsPurchases {...insight} />}
            <SalesHeatmap today={today} />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              mt: 2.5,
              gridTemplateColumns: {
                xs: 'minmax(0,1fr)',
                md: 'repeat(2, minmax(0,1fr))',
                lg: 'repeat(3, minmax(0,1fr))',
              },
            }}
          >
            {can('payments.view') && <PaymentMethods {...insight} />}
            <TopCustomers {...insight} />
            {can('reports.financial') && <MarginTrend {...insight} />}
          </Box>
        </>
      )}
      {!reports && (
        <Box sx={{ mt: 2.5 }}>
          <RemindersPanel />
        </Box>
      )}
    </>
  );
}

type Tone = 'primary' | 'success' | 'warning' | 'info' | 'error';

function Kpi({
  label,
  value,
  title,
  hint,
  hintTone,
  change,
  changeHint,
  icon,
  tone,
  loading,
  spark,
  hidden,
}: {
  label: string;
  value: string | undefined | false;
  title?: string | undefined | false;
  hint: string | undefined | false;
  hintTone?: 'error' | undefined;
  change?: number | null;
  changeHint?: string;
  icon: ReactNode;
  tone: Tone;
  loading: boolean;
  spark?: number[];
  hidden?: boolean;
}) {
  if (hidden) return null;
  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        transition: 'box-shadow 160ms',
        '&:hover': { boxShadow: '0 12px 24px -6px rgba(16,24,40,0.10)' },
      }}
    >
      <Box
        aria-hidden
        sx={(t) => ({
          position: 'absolute',
          right: -36,
          top: -36,
          width: 130,
          height: 130,
          borderRadius: '50%',
          background: `radial-gradient(circle, color-mix(in srgb, ${(t.vars ?? t).palette[tone].main} 16%, transparent), transparent 70%)`,
        })}
      />
      <CardContent sx={{ position: 'relative' }}>
        <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550 }}>
            {label}
          </Typography>
          <IconTile color={tone} size={38}>
            {icon}
          </IconTile>
        </Stack>
        {loading ? (
          <Skeleton width="60%" height={44} />
        ) : (
          <Tooltip title={title || ''} placement="bottom-start">
            <Typography variant="h3" className="tabular" sx={{ mt: 0.25, width: 'fit-content' }}>
              {value || '—'}
            </Typography>
          </Tooltip>
        )}
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minHeight: 24, mt: 0.25 }}>
          {loading ? (
            <Skeleton width="50%" />
          ) : (
            <>
              {change !== undefined && change !== null && <ChangeBadge change={change} />}
              <Typography
                variant="caption"
                color={hintTone === 'error' ? 'error.main' : 'text.secondary'}
                noWrap
                sx={{ fontWeight: hintTone ? 650 : undefined }}
              >
                {change !== undefined && change !== null ? changeHint : hint || ' '}
              </Typography>
            </>
          )}
        </Stack>
        {!loading && change !== undefined && change !== null && hint && (
          <Typography variant="caption" color="text.secondary" component="div">
            {hint}
          </Typography>
        )}
        {spark && spark.length > 1 && !loading && (
          <Box sx={{ mx: -2, mb: -2, mt: 1, height: 44 }}>
            <SparkLineChart
              data={spark}
              height={44}
              area
              curve="monotoneX"
              color="var(--mui-palette-primary-main)"
              margin={{ top: 4, bottom: 0, left: 0, right: 0 }}
              sx={{
                '& .MuiLineChart-area': { fillOpacity: 0.12 },
                '& .MuiLineChart-line': { strokeWidth: 2 },
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function ChangeBadge({ change }: { change: number }) {
  const up = change > 0.0005;
  const down = change < -0.0005;
  const color = up ? 'success' : down ? 'error' : 'info';
  const Icon = up ? TrendingUpRounded : down ? TrendingDownRounded : TrendingFlatRounded;
  return (
    <Box
      className="tabular"
      sx={(t) => {
        const c = (t.vars ?? t).palette[color].main;
        return {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          px: 0.75,
          height: 22,
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 700,
          color: c,
          bgcolor: `color-mix(in srgb, ${c} 12%, transparent)`,
          flexShrink: 0,
        };
      }}
    >
      <Icon sx={{ fontSize: 15 }} />
      {formatChange(change)}
    </Box>
  );
}

export function Panel({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card sx={{ minWidth: 0 }}>
      <CardContent>
        <Stack
          direction="row"
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5">{title}</Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function ViewAll({ to, label = 'View all' }: { to: string; label?: string }) {
  const navigate = useNavigate();
  return (
    <Button
      size="small"
      endIcon={<ArrowForwardRounded />}
      onClick={() => void navigate(to)}
      sx={{ color: 'text.secondary', flexShrink: 0 }}
    >
      {label}
    </Button>
  );
}

function Quiet({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <Stack spacing={1} sx={{ py: 4, alignItems: 'center', textAlign: 'center' }}>
      {icon && <Box sx={{ color: 'success.main', '& svg': { fontSize: 32 } }}>{icon}</Box>}
      <Typography color="text.secondary">{children}</Typography>
    </Stack>
  );
}

function Rows({ n = 4 }: { n?: number }) {
  return (
    <Stack spacing={1.5}>
      {Array.from({ length: n }, (_, i) => (
        <Skeleton key={i} variant="rounded" height={40} />
      ))}
    </Stack>
  );
}

type SalesRow = { label: string; periodStart: string; total: string; invoiceCount: number };

function SalesTrend({
  loading,
  rows,
  previous,
  interval,
  rangeLabel,
}: {
  loading: boolean;
  rows: SalesRow[];
  previous: SalesRow[];
  interval: 'day' | 'week';
  rangeLabel: string;
}) {
  const { format } = useShop();
  const hasData = rows.some((r) => Number(r.total) !== 0);
  const prev = rows.map((_, i) => {
    // Align the previous period point-for-point from the end.
    const p = previous[previous.length - rows.length + i];
    return p ? Number(p.total) : null;
  });
  const showPrev = prev.some((v) => v !== null && v !== 0);
  return (
    <Panel
      title="Sales trend"
      subtitle={`${interval === 'day' ? 'Daily' : 'Weekly'} invoiced total · last ${rangeLabel}`}
      action={
        showPrev && (
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <LegendKey label="This period" />
            <LegendKey label="Previous" dashed />
          </Stack>
        )
      }
    >
      {loading ? (
        <Skeleton variant="rounded" height={340} />
      ) : !hasData ? (
        <Box
          sx={{
            height: 340,
            display: 'grid',
            placeItems: 'center',
            borderRadius: '12px',
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Stack spacing={0.5} sx={{ alignItems: 'center', textAlign: 'center', px: 3 }}>
            <TrendingUpRounded sx={{ color: 'text.disabled', fontSize: 32 }} />
            <Typography sx={{ fontWeight: 650 }}>No sales in this period yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Your daily totals will draw here as soon as you post invoices.
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Box sx={{ height: 340, mx: -1 }}>
          <LineChart
            height={340}
            margin={{ left: 8, right: 16, top: 16, bottom: 8 }}
            grid={{ horizontal: true }}
            hideLegend
            xAxis={[
              {
                scaleType: 'point',
                data: rows.map((d) => shortDate(d.periodStart, format.locale)),
                tickLabelStyle: { fontSize: 11 },
                disableLine: true,
                disableTicks: true,
              },
            ]}
            yAxis={[
              {
                valueFormatter: (v: number | null) => moneyCompact(v ?? 0, format),
                tickLabelStyle: { fontSize: 11 },
                disableLine: true,
                disableTicks: true,
                width: 64,
              },
            ]}
            series={[
              ...(showPrev
                ? [
                    {
                      id: 'prev',
                      label: 'Previous period',
                      data: prev,
                      showMark: false,
                      curve: 'monotoneX' as const,
                      color: 'var(--mui-palette-text-disabled)',
                      valueFormatter: (v: number | null) => money(v ?? 0, format),
                    },
                  ]
                : []),
              {
                id: 'current',
                label: 'Sales',
                data: rows.map((d) => Number(d.total)),
                area: true,
                showMark: false,
                curve: 'monotoneX' as const,
                color: 'var(--mui-palette-primary-main)',
                valueFormatter: (v: number | null) => money(v ?? 0, format),
              },
            ]}
            sx={{
              '& .MuiLineChart-area': { fillOpacity: 0.14 },
              '& .MuiLineChart-line': { strokeWidth: 2 },
              '& .MuiLineChart-line[data-series="prev"]': {
                strokeDasharray: '5 4',
                strokeWidth: 1.5,
              },
              '& .MuiChartsGrid-line': { strokeDasharray: '4 4', opacity: 0.5 },
            }}
          />
        </Box>
      )}
    </Panel>
  );
}

function LegendKey({ label, dashed }: { label: string; dashed?: boolean }) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
      <Box
        sx={{
          width: 16,
          height: 0,
          borderTop: '2px solid',
          borderColor: dashed ? 'text.disabled' : 'primary.main',
          borderTopStyle: dashed ? 'dashed' : 'solid',
        }}
      />
      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>
    </Stack>
  );
}

const BUCKET_COLORS: Record<AgingBucket['tone'], string> = {
  success: 'success.main',
  warning: '#FDB022',
  orange: '#F79009',
  error: '#F04438',
  critical: '#B42318',
};

type Aging = {
  data: {
    contactId: string;
    name: string;
    phone: string | null;
    outstanding: string;
    over90: string;
    oldestDueDate: string | null;
  }[];
  totals: AgingTotals;
  page: { total: number };
};

function AgingPanel({
  receivable,
  payable,
  loading,
}: {
  receivable: Aging | undefined;
  payable: Aging | undefined;
  loading: boolean;
}) {
  const { format } = useShop();
  const [ledger, setLedger] = useState<'RECEIVABLE' | 'PAYABLE'>('RECEIVABLE');
  const report = ledger === 'RECEIVABLE' ? receivable : payable;
  const buckets = report ? agingBuckets(report.totals) : [];
  const total = Number(report?.totals.outstanding ?? 0);
  const party = ledger === 'RECEIVABLE' ? 'customer' : 'supplier';

  return (
    <Panel
      title={ledger === 'RECEIVABLE' ? 'Receivables aging' : 'Payables aging'}
      subtitle={ledger === 'RECEIVABLE' ? 'What customers owe you' : 'What you owe suppliers'}
      action={
        <ToggleButtonGroup
          exclusive
          size="small"
          value={ledger}
          onChange={(_, v: typeof ledger | null) => v && setLedger(v)}
          aria-label="Ledger"
          sx={{
            '& .MuiToggleButton-root': {
              px: 1.25,
              py: 0.25,
              textTransform: 'none',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            },
          }}
        >
          <ToggleButton value="RECEIVABLE">Owed to you</ToggleButton>
          <ToggleButton value="PAYABLE">You owe</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      {loading ? (
        <Rows n={5} />
      ) : !report || total <= 0 ? (
        <Quiet icon={<CheckCircleRounded />}>
          {ledger === 'RECEIVABLE'
            ? 'No customer balances — everyone is paid up.'
            : 'No open supplier bills.'}
        </Quiet>
      ) : (
        <Stack spacing={2.25}>
          <Box>
            <Typography variant="h4" className="tabular">
              {money(total, format)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              across {report.page.total} {party}
              {report.page.total === 1 ? '' : 's'}
            </Typography>
          </Box>
          <Stack
            direction="row"
            aria-label="Aging breakdown"
            role="img"
            sx={{ height: 12, borderRadius: 999, overflow: 'hidden', gap: '2px' }}
          >
            {buckets
              .filter((b) => b.amount > 0)
              .map((b) => (
                <Tooltip key={b.key} describeChild title={`${b.label}: ${money(b.amount, format)}`}>
                  <Box
                    sx={{
                      width: `${String(b.share * 100)}%`,
                      minWidth: 4,
                      bgcolor: BUCKET_COLORS[b.tone],
                    }}
                  />
                </Tooltip>
              ))}
          </Stack>
          <Box
            component="dl"
            sx={{
              m: 0,
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              rowGap: 0.75,
              columnGap: 2,
            }}
          >
            {buckets.map((b) => (
              <Box key={b.key} sx={{ display: 'contents' }}>
                <Stack component="dt" direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: BUCKET_COLORS[b.tone],
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {b.label}
                  </Typography>
                </Stack>
                <Typography
                  component="dd"
                  variant="body2"
                  className="tabular"
                  sx={{
                    m: 0,
                    textAlign: 'right',
                    fontWeight: b.amount ? 650 : 400,
                    color: b.amount ? 'text.primary' : 'text.disabled',
                  }}
                >
                  {money(b.amount, format)}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
            <Typography variant="overline" color="text.secondary">
              Largest balances
            </Typography>
            <Stack spacing={1} sx={{ mt: 0.5 }}>
              {report.data.slice(0, 4).map((r) => (
                <Stack
                  key={r.contactId}
                  direction="row"
                  spacing={2}
                  sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                      {r.name}
                    </Typography>
                    {Number(r.over90) > 0 && (
                      <Typography variant="caption" color="error.main">
                        {money(r.over90, format)} over 90 days
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    className="tabular"
                    sx={{ fontWeight: 650, flexShrink: 0 }}
                  >
                    {money(r.outstanding, format)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Stack>
      )}
    </Panel>
  );
}

type LowStock = {
  data: {
    variantId: string;
    sku: string;
    name: string;
    unitSymbol: string;
    reorderLevel: string;
    available: string;
    onOrder: string;
  }[];
  page: { total: number };
};

function LowStockPanel({ loading, data }: { loading: boolean; data: LowStock | undefined }) {
  const { format } = useShop();
  return (
    <Panel
      title="Running low"
      subtitle={
        data?.page.total
          ? `${String(data.page.total)} items at or below reorder level`
          : 'Below the reorder level'
      }
      action={<ViewAll to="/inventory" />}
    >
      {loading ? (
        <Rows />
      ) : data?.data.length ? (
        <Stack spacing={1.75}>
          {data.data.map((r) => {
            const sev = stockSeverity(r.available, r.reorderLevel);
            const color = sev === 'low' ? 'warning.main' : 'error.main';
            return (
              <Box key={r.variantId}>
                <Stack direction="row" spacing={2} sx={{ alignItems: 'baseline' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                      {r.name}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    className="tabular"
                    sx={{ fontWeight: 700, color, flexShrink: 0 }}
                  >
                    {sev === 'out' ? 'Out of stock' : `${qty(r.available, format)} ${r.unitSymbol}`}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    mt: 0.75,
                    height: 6,
                    borderRadius: 999,
                    bgcolor: 'action.hover',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${String(stockFill(r.available, r.reorderLevel) * 100)}%`,
                      height: '100%',
                      borderRadius: 999,
                      bgcolor: color,
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {r.sku} · reorder at {qty(r.reorderLevel, format)}
                  {Number(r.onOrder) > 0 && ` · ${qty(r.onOrder, format)} on order`}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Quiet icon={<CheckCircleRounded />}>Stock levels look healthy.</Quiet>
      )}
    </Panel>
  );
}

const SEVERITY = {
  INFO: { icon: <InfoRounded />, color: 'info' as const },
  WARNING: { icon: <WarningAmberRounded />, color: 'warning' as const },
  CRITICAL: { icon: <ErrorRounded />, color: 'error' as const },
};

/** Unread in-app reminders (overdue invoices, low stock, expiring quotations…) for this user. */
function RemindersPanel() {
  const qc = useQueryClient();
  const notify = useNotify();
  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'dashboard'],
    queryFn: () =>
      unwrap(api.GET('/notifications', { params: { query: { unread: 'true', pageSize: 5 } } })),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['notifications'] });
  const markRead = async (id: string) => {
    try {
      await unwrap(api.POST('/notifications/{id}/read', { params: { path: { id } } }));
      await refresh();
    } catch (e) {
      notify.error(e);
    }
  };
  const markAll = async () => {
    try {
      await unwrap(api.POST('/notifications/read-all'));
      await refresh();
      notify.success('All reminders marked as read');
    } catch (e) {
      notify.error(e);
    }
  };
  const unread = data?.unreadCount ?? 0;

  return (
    <Panel
      title="Reminders"
      subtitle={unread ? `${String(unread)} unread` : 'Due dates, stock and more'}
      action={
        unread > 0 && (
          <Button
            size="small"
            startIcon={<DoneAllRounded />}
            onClick={() => void markAll()}
            sx={{ color: 'text.secondary', flexShrink: 0 }}
          >
            Mark all read
          </Button>
        )
      }
    >
      {isLoading ? (
        <Rows />
      ) : data?.data.length ? (
        <Stack spacing={1.25}>
          {data.data.map((n) => {
            const s = SEVERITY[n.severity];
            return (
              <Stack
                key={n.id}
                direction="row"
                spacing={1.5}
                sx={{
                  alignItems: 'flex-start',
                  p: 1.25,
                  borderRadius: '12px',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <IconTile size={32} color={s.color}>
                  {s.icon}
                </IconTile>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 650 }}>
                    {n.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {n.body}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {relativeTime(n.createdAt)}
                  </Typography>
                </Box>
                <Tooltip title="Mark as read">
                  <IconButton
                    size="small"
                    aria-label={`Mark “${n.title}” as read`}
                    onClick={() => void markRead(n.id)}
                  >
                    <CheckCircleRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <Quiet icon={<NotificationsNoneRounded />}>You’re all caught up.</Quiet>
      )}
    </Panel>
  );
}

function BestSellers({
  loading,
  rows,
  rangeLabel,
}: {
  loading: boolean;
  rows:
    { variantId: string; rank: number; name: string; revenue: string; qty: string }[] | undefined;
  rangeLabel: string;
}) {
  const { format } = useShop();
  const top = Number(rows?.[0]?.revenue ?? 1) || 1;
  return (
    <Panel
      title="Best sellers"
      subtitle={`By revenue · last ${rangeLabel}`}
      action={<ViewAll to="/reports" />}
    >
      {loading ? (
        <Rows n={5} />
      ) : rows?.length ? (
        <Stack spacing={1.75}>
          {rows.map((r) => (
            <Box key={r.variantId}>
              <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                  <Box component="span" className="tabular" sx={{ color: 'text.disabled', mr: 1 }}>
                    {r.rank}
                  </Box>
                  {r.name}
                </Typography>
                <Typography
                  variant="body2"
                  className="tabular"
                  sx={{ fontWeight: 650, flexShrink: 0 }}
                >
                  {money(r.revenue, format)}
                </Typography>
              </Stack>
              <Box
                sx={{ height: 6, borderRadius: 999, bgcolor: 'action.hover', overflow: 'hidden' }}
              >
                <Box
                  sx={{
                    width: `${String((Number(r.revenue) / top) * 100)}%`,
                    height: '100%',
                    borderRadius: 999,
                    bgcolor: 'primary.main',
                  }}
                />
              </Box>
            </Box>
          ))}
        </Stack>
      ) : (
        <Quiet>No sales in this period yet.</Quiet>
      )}
    </Panel>
  );
}

/** A slim banner when the subscription needs attention (expiring, grace, locked, suspended). */
/** `21 Sep` for chart axes (business dates, no time-zone shift). */
function shortDate(date: string, locale: string): string {
  const [y, m, d] = date.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return date;
  try {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(y, m - 1, d)));
  } catch {
    return date.slice(5, 10);
  }
}
